import { defineStore } from "pinia";
import {allModals} from '../config/allModals';
import {sendReport} from "@/services/reportService";
import type { FormDataType, serviceType} from '@/types/form';

export const useAppStore = defineStore("appStore", {
    state: (): {
        appVersion: string;
        service: serviceType;
        overlayVisible: boolean;

        allModals: any;
        modalData:{
            show: boolean;
            modalVariant: string;
            errorMessage: string;
            commentsID: string[];
            reason?: string;
            decisionBase?: string;
            newStatus?: string;
            metaIds?: string[];
            violationReportId?: string;
        };

        isButtonDisabled: boolean;
        inputDataSource: string;

        form:{
            show: boolean;
            reportCommentID: string[];
        };
        formData: {
            source: string;
            commentId: string;
            url: string;
            reason: string;
            explain: string;
            userEmail: string;
            coreEmail: string;
            isEmailAdded: boolean;
            userName: string;
            rules: string;
            ptoken: string,
            site_name: string,
            thread_id: string,
            object_id: string,
            userLogin: string;
            isLogged: boolean;
            isUserChangeEmail: boolean;
        };

        isActionFormVisible: boolean;
        actionFormVariant: string;
        actionFormData: any;
        complaintCode: string;
        complaintReason: string;


    } => ({
        appVersion: "",
        service: "gazeta_pl",
        overlayVisible: false,

        allModals: {
            forum: allModals.forum,
            gazeta_pl: allModals.gazeta_pl,
            wyborcza: allModals.wyborcza,
            sport: allModals.wyborcza,  
        },
        modalData: {
            show: false,
            modalVariant: "thankYou_notEmail",
            errorMessage: "Coś poszło nie tak. Spróbuj ponownie później.",
            commentsID: [""],
            reason: "",
            decisionBase: "",
            newStatus: "",
            metaIds: [],
            violationReportId: "",
        },

        isButtonDisabled: false,
        inputDataSource: 'form',

        form:{
            show: false,
            reportCommentID: [""],
        },
        formData: {
            source: "",
            commentId: "",
            url: "",
            reason: "",
            explain: "",
            userEmail: "",
            coreEmail: "",
            isEmailAdded: false,
            userName: "",
            rules: "",
            ptoken: "",
            site_name: "",
            thread_id: "",
            object_id: "",
            userLogin: "",
            isLogged: false,
            isUserChangeEmail: false,
        },

        isActionFormVisible: false,
        actionFormVariant: "delete",
        actionFormData: {},
        complaintCode: "",
        complaintReason: "",
    }),
    getters:{
        getFormData: (state) => state.formData,
        getAppVersion: (state) => state.appVersion,
        getService: (state) => state.service,
        isOverlayVisible: (state) => state.overlayVisible,
        getModalData: (state) => state.modalData,
        getFormVisibility: (state) => state.form.show,
        getModal: (state) => (variant:string) => {
            const serviceModals = state.allModals[state.service];
            if (!serviceModals) {
                console.warn(`No modals -> ${variant} found for service: ${state.service}`);
                return null;
            }
            return serviceModals[variant];
        },
        getButtonDisabled: (state) => state.isButtonDisabled,
        getInputDataSource: (state) => state.inputDataSource,
        getReportCommentID: (state) => state.form.reportCommentID,
        getActionFormVariant: (state) => state.actionFormVariant,
        getActionFormVisible: (state) => state.isActionFormVisible,
        getModalVisibility: (state) => state.modalData.show,
        getActionFormData: (state) => state.actionFormData,
        getComplaintCode: (state) => state.complaintCode,
        getComplaintReason: (state) => state.complaintReason,
    },
    actions: {
        setAppVersion(version: string) {
            this.appVersion = version;
        },
        setService(service: serviceType) {
            this.service = service;
        },
        setOverlayVisible(visible: boolean) {
            this.overlayVisible = visible;
        },
        setFormVisibility(visible: boolean) {
            this.form.show = visible;
        },
        
        setModalData(data:any) {
            this.modalData = {...this.modalData, ...data};
        },
        setModalVisibility(visible: boolean) {
            this.modalData.show = visible;
        },
        closeElements(type: string) {
            if (type === "dsa_modal") {
                this.setModalVisibility(false);
            } else if (type === "dsa_form") {
                this.setFormVisibility(false);
            } else if (type === "dsa_action") {
                this.setOverlayVisible(false);
                this.setActionFormVisible(false);
                this.setModalVisibility(false);
            } else {
                this.setOverlayVisible(false);
                this.setModalVisibility(false);
                this.setFormVisibility(false);
                this.setActionFormVisible(false);
                this.complaintReason = ""; 
            }
        },
        setButtonDisabled(disabled: boolean) {
            this.isButtonDisabled = disabled;
        },
        setInputDataSource(value: string){
            this.inputDataSource = value;
        },
        setReportCommentID(commentsID: string[]) {
            this.form.reportCommentID = commentsID;
        },
        setActionFormVariant(value: string) {
            this.actionFormVariant = value;
        },
        setActionFormVisible(visible: boolean) {
            this.isActionFormVisible = visible;
        },
        async submitForm() {
            const data = this.getFormData;
            if(!data){
                console.error("Brak danych formularza. Nie można wysłać zgłoszenia.");
                return;
            }
            this.setButtonDisabled(true);
            this.sendData(data).then(response => {

                //dodac pozostałe dane do modalData jeśli będą potrzebne w modalu lukixp
                const modalData = {
                    show: true,
                    modalVariant: response.variant,
                    commentsID: response.commentId,
                    errorMessage: response.errorMessage,
                };

                if(response.variant === "something_wrong"){
                    console.warn('Formularz nie został wysłany. Spróbój ponownie.');
                } 
                
                this.setFormVisibility(false);
                this.setButtonDisabled(false);
                this.setModalData(modalData);
            });
        },
        setSingleFormField<K extends keyof FormDataType>(field: K, value: FormDataType[K]) {
            this.formData[field] = value;
        },
        setFormData(data: any) {
            this.formData = {
                ...this.formData,
                ...data
            };
        },
        async sendData(data: any) {
            let variant = "";
            let errorMessage = "";
            
            data = {
                ...data,
                userName: data.userName || data.userLogin ||  "",
            };

            console.log('---DANE FORMULARZA---', data);
        
            try {
                const response = await sendReport(data);
                
                if (response.status === 200) {
                    if(data.userEmail === '' || !data.isUserChangeEmail){
                        variant = "thankYou_notEmail"
                    } else{
                        variant = 'thankYou_haveEmail'
                    }
                } else if (response.status === 400) {
                    variant = "something_wrong";
                    errorMessage = "Błędny adres mailowy. Sprawdź adres mailowy.";
                } else if (response.status === 502) {
                    variant = "something_wrong";
                    errorMessage="Nie możemy wysłac do Ciebie maila.";
                } else if (response.status === 409) {
                    variant = "already_submit";
                    errorMessage = "";
                } else if (response.status === 422) {
                    console.log('Komentarz, który próbujesz zgłosić, został już usunięty.');
                    variant = "comment_already_deleted";
                    errorMessage = "";
                } else {
                    console.warn('Formularz wysłany ale nie udało sie utworzyć zgłoszenia.');
                    variant = "something_wrong";
                    errorMessage = "Coś poszło nie tak. Spróbuj ponownie później.";
                }
            } catch (err) {
                console.error("Błąd przy wysyłaniu formularza:", err);
                variant = "something_wrong";
                errorMessage = "Wystąpił błąd podczas wysyłania danych z formularza." + err;
            }
        
            return {
                variant,
                errorMessage,
                commentId: data?.commentId || []
            }
        },
        setActionFormData(data: any) {
            this.actionFormData = {
                ...this.actionFormData,
                ...data
            };
        },
        sendActionFormData() {
            // tutaj możesz dodać logikę wysyłania danych z formularza moderatora do backendu
            const data = this.actionFormData;
            console.log("Dane z formularza moderatora do wysłania(FORUM):", data);
            this.closeElements("dsa_action");
        },
        setComplaintCode(code: string) {
            this.complaintCode = code;
        },
        setComplaintReason(value: string) { 
            this.complaintReason = value; 
        },
    }  
 });